import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import api from "@/services/api";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { useMyContext } from "@/context/ContextApi";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { KeyRound } from "lucide-react";

const ForgotPassword = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { token } = useMyContext();

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: { email: "" },
    mode: "onTouched",
  });

  const onPasswordForgotHandler = async (data) => {
    try {
      setLoading(true);
      await api.post("/auth/public/forgot-password", { email: data.email });
      reset();
      toast.success(t('forgotPassword_successMessage'));
    } catch {
      toast.error(t('forgotPassword_errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) navigate("/");
  }, [token, navigate]);

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">

        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-violet-500/10 border border-violet-500/20 mb-4">
            <KeyRound size={22} className="text-violet-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">{t('forgotPassword_title')}</h1>
          <p className="mt-1 text-sm text-zinc-500">{t('forgotPassword_subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit(onPasswordForgotHandler)} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-zinc-400 text-xs uppercase tracking-wider">{t('forgotPassword_emailLabel')}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t('forgotPassword_emailPlaceholder')}
              className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-violet-500/50 focus-visible:border-violet-500/50"
              {...register("email", { required: true })}
            />
            {errors?.email && <p className="text-xs text-red-400">{t('forgotPassword_emailRequired')}</p>}
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-violet-600 hover:bg-violet-500 text-white font-semibold"
          >
            {loading ? t('forgotPassword_submitButtonLoading') : t('forgotPassword_submitButton')}
          </Button>
        </form>

        <p className="mt-5 text-center text-xs text-zinc-600">
          <Link to="/login" className="text-zinc-400 hover:text-white transition-colors">
            {t('forgotPassword_backToSignin')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
